/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { take } from 'rxjs/operators';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import {
  IListado,
  IUsuario,
  IQueryParam,
  ISocketMessage,
  IDispositivo,
  IEstablecimiento,
  IAlerta,
  ICliente,
  IBombeoCanal,
  IBombeoArrancador,
  IComando,
  IReporte,
  ITipoDispositivo,
  IComedero,
  ILote,
  IAmbiente,
  IAnimal,
  ICisterna,
  IEstacionMeteorologica,
  TipoDispositivo,
  ILamina,
  IPluviometro,
  IFreatimetro,
  ICanal,
  ISensorHumedadSuelo,
  IZona,
  ISilobolsa,
  IBombaSumergible,
  IEvento,
  IRepresa,
  ISuceso,
  ISensorRPM,
  IConfigNotificacion,
  ILuminaria,
  ICanieria,
  IDispositivoWithRegistros,
  ILanzaHumedad,
} from 'modelos/src';
import { WebSocketService } from './websocket';
import { ClientesService } from '../modulos/basicos/clientes/service';
import { UsuariosService } from '../modulos/basicos/usuarios/service';
import { DispositivosService } from '../modulos/basicos/dispositivos/service';
import { EstablecimientosService } from '../modulos/basicos/establecimientos/service';
import { AlertasService } from '../modulos/basicos/alertas/service';
import { BombeoService } from '../modulos/aplicaciones/bombeo/service';
import { ComandosService } from '../modulos/basicos/comandos/service';
import { ReportesService } from '../modulos/basicos/reportes/service';
import { TipoDispositivosService } from '../modulos/basicos/tipoDispositivos/service';
import { ComederosService } from '../modulos/aplicaciones/comederos/service';
import { LotesService } from '../modulos/basicos/lotes/service';
import { AmbientesService } from '../modulos/basicos/ambientes/service';
import { CaravanasService } from '../modulos/aplicaciones/caravana/service';
import { RepresasService } from '../modulos/aplicaciones/represas/service';
import { CisternasService } from '../modulos/aplicaciones/cisterna/service';
import { IAPIkey, IGetApiKey } from 'modelos/src';
import { apiKeyService } from '../modulos/basicos/apiKey/service';
import { EstacionMeteorologicasService } from '../modulos/aplicaciones/estacion-meteorologica/service';
import { SlalService } from '../modulos/aplicaciones/sla/service';
import { PluviometrosService } from '../modulos/aplicaciones/pluviometro/service';
import { FreatimetrosService } from '../modulos/aplicaciones/freatimetro/service';
import { CanalesService } from '../modulos/aplicaciones/canal/service';
import { ShsService } from '../modulos/aplicaciones/shs/service';
import { ZonasService } from '../modulos/basicos/zonas/service';
import { SilobolsasService } from '../modulos/aplicaciones/silobolsas/service';
import { SumergiblesService } from '../modulos/aplicaciones/bombaSumergibles/service';
import { EventosService } from '../modulos/basicos/eventos/service';
import { SucesosService } from '../modulos/aplicaciones/caravana/sucesos.service';
import { SensorRPMsService } from '../modulos/aplicaciones/rpms/service';
import { ConfigNotificacionService } from '../modulos/basicos/config-notificacion/service';
import { LuminariasService } from '../modulos/aplicaciones/luminarias/service';
import { CanieriaService } from '../modulos/aplicaciones/caudal/service';
import { BombeoV2Service } from '../modulos/aplicaciones/bombeo-v2/service';
import { LanzaHumedadService } from '../modulos/aplicaciones/lanza-humedad/service';

type Tipo =
  | ICliente
  | ICliente[]
  | IUsuario
  | IListado<IUsuario>
  | IGetApiKey
  | IListado<IGetApiKey>
  | IDispositivo
  | IListado<IDispositivo>
  | IDispositivoWithRegistros
  | IListado<IDispositivoWithRegistros>
  | IEstablecimiento
  | IListado<IEstablecimiento>
  | ILote
  | IListado<ILote>
  | IZona
  | IListado<IZona>
  | IAlerta
  | IListado<IAlerta>
  | IEvento
  | IListado<IEvento>
  | IComando
  | IListado<IComando>
  | IReporte
  | IListado<IReporte>
  | ITipoDispositivo
  | IListado<ITipoDispositivo>
  | IBombeoCanal
  | IListado<IBombeoCanal>
  | IBombeoArrancador
  | IListado<IBombeoArrancador>
  | IComedero
  | IListado<IComedero>
  | IAnimal
  | IListado<IAnimal>
  | ICisterna
  | IListado<ICisterna>
  | IEstacionMeteorologica
  | IListado<IEstacionMeteorologica>
  | ILamina
  | IListado<ILamina>
  | IPluviometro
  | IListado<IPluviometro>
  | IFreatimetro
  | IListado<IFreatimetro>
  | ICanal
  | IListado<ICanal>
  | ISensorHumedadSuelo
  | IListado<ISensorHumedadSuelo>
  | ISilobolsa
  | IListado<ISilobolsa>
  | IBombaSumergible
  | IListado<IBombaSumergible>
  | IRepresa
  | IListado<IRepresa>
  | ISuceso
  | IListado<ISuceso>
  | ISensorRPM
  | IListado<ISensorRPM>
  | ILuminaria
  | IListado<ILuminaria>
  | ICanieria
  | IListado<ICanieria>
  | ILanzaHumedad
  | IListado<ILanzaHumedad>;

class RequestQueue {
  subscribe: Subject<Tipo>;
  requests: number;
  cache?: Tipo;

  constructor() {
    this.requests = 0;
    this.subscribe = new Subject<Tipo>();
    this.cache = undefined;
  }
}

interface IRequestId {
  fn: (id: string) => Promise<any>;
  keys: { [key: string]: RequestQueue };
}

interface IRequestQuery {
  fn: (query: IQueryParam) => Promise<any>;
  keys: { [key: string]: RequestQueue };
}

interface IEntidades {
  cliente: IRequestId;
  clientes: IRequestQuery;
  usuario: IRequestId;
  usuarios: IRequestQuery;
  establecimiento: IRequestId;
  establecimientos: IRequestQuery;
  lote: IRequestId;
  lotes: IRequestQuery;
  ambiente: IRequestId;
  ambientes: IRequestQuery;
  zona: IRequestId;
  zonas: IRequestQuery;
  apikey: IRequestId;
  apikeys: IRequestQuery;
  tipodispositivo: IRequestId;
  tipodispositivoByNombre: IRequestId;
  tipodispositivos: IRequestQuery;
  //
  comando: IRequestId;
  comandos: IRequestQuery;
  comandosEstacionMeteorologica: IRequestQuery;
  comandosPluviometro: IRequestQuery;
  comandosFreatimetro: IRequestQuery;
  //
  dispositivo: IRequestId;
  dispositivoByDeveui: IRequestId;
  dispositivos: IRequestQuery;
  dispositivosComedero: IRequestQuery;
  dispositivosBombeo: IRequestQuery;
  dispositivosCanal: IRequestQuery;
  dispositivosRepresa: IRequestQuery;
  dispositivosCaravana: IRequestQuery;
  dispositivosBombaCisterna: IRequestQuery;
  dispositivosNivelCisterna: IRequestQuery;
  dispositivosSla: IRequestQuery;
  dispositivosEstacionMeteorologica: IRequestQuery;
  dispositivosPluviometro: IRequestQuery;
  dispositivosFreatimetro: IRequestQuery;
  dispositivosShs: IRequestQuery;
  dispositivosSilobolsa: IRequestQuery;
  dispositivosBombeoV2: IRequestQuery;
  dispositivosBombeoV2UltimosRegistros: IRequestQuery;
  dispositivosSensorRPM: IRequestQuery;
  dispositivosLuminarias: IRequestQuery;
  dispositivosCanierias: IRequestQuery;
  dispositivosPresionAgua: IRequestQuery;
  dispositivosLanzaHumedad: IRequestQuery;
  //
  alerta: IRequestId;
  alertas: IRequestQuery;
  alertasComedero: IRequestQuery;
  alertasBombeo: IRequestQuery;
  alertasCanal: IRequestQuery;
  alertasRepresa: IRequestQuery;
  alertasCaravana: IRequestQuery;
  alertasBombaCisterna: IRequestQuery;
  alertasNivelCisterna: IRequestQuery;
  alertasSla: IRequestQuery;
  alertasEstacionMeteorologica: IRequestQuery;
  alertasPluviometro: IRequestQuery;
  alertasFreatimetro: IRequestQuery;
  alertasSilobolsa: IRequestQuery;
  alertasLuminarias: IRequestQuery;
  alertasCanierias: IRequestQuery;
  alertasLanzaHumedad: IRequestQuery;
  //
  evento: IRequestId;
  eventos: IRequestQuery;
  //
  reporte: IRequestId;
  reportes: IRequestQuery;
  reportesComedero: IRequestQuery;
  reportesBombeo: IRequestQuery;
  reportesCanal: IRequestQuery;
  reportesRepresa: IRequestQuery;
  reportesCaravana: IRequestQuery;
  reportesCaravanaMyT: IRequestQuery;
  reportesBombaCisterna: IRequestQuery;
  reportesNivelCisterna: IRequestQuery;
  reportesSla: IRequestQuery;
  reportesEstacionMeteorologica: IRequestQuery;
  reportesPluviometro: IRequestQuery;
  reportesFreatimetro: IRequestQuery;
  reportesShs: IRequestQuery;
  reportesSilobolsa: IRequestQuery;
  reportesCanalBombeoV2: IRequestQuery;
  reportesSensorRPM: IRequestQuery;
  reportesLuminarias: IRequestQuery;
  reportesCanierias: IRequestQuery;
  reportesPresionAgua: IRequestQuery;
  reportesLanzaHumedad: IRequestQuery;
  //
  bombeocanal: IRequestId;
  bombeocanals: IRequestQuery;
  bombeoarrancador: IRequestId;
  bombeoarrancadors: IRequestQuery;
  comedero: IRequestId;
  comederos: IRequestQuery;
  animal: IRequestId;
  animals: IRequestQuery;
  cisterna: IRequestId;
  cisternas: IRequestQuery;
  estacionMeteorologica: IRequestId;
  estacionMeteorologicas: IRequestQuery;
  lamina: IRequestId;
  laminas: IRequestQuery;
  pluviometro: IRequestId;
  pluviometros: IRequestQuery;
  freatimetro: IRequestId;
  freatimetros: IRequestQuery;
  canal: IRequestId;
  canales: IRequestQuery;
  sensorHumedadSuelo: IRequestId;
  sensorHumedadSuelos: IRequestQuery;
  silobolsa: IRequestId;
  silobolsas: IRequestQuery;
  bombaSumergible: IRequestId;
  bombaSumergibles: IRequestQuery;
  represa: IRequestId;
  represas: IRequestQuery;
  suceso: IRequestId;
  sucesos: IRequestQuery;
  sensorRPM: IRequestId;
  sensorRPMs: IRequestQuery;
  configNotificacion: IRequestId;
  configNotificacions: IRequestQuery;
  luminaria: IRequestId;
  luminarias: IRequestQuery;
  canieria: IRequestId;
  canierias: IRequestQuery;
  lanzaHumedad: IRequestId;
  lanzasHumedad: IRequestQuery;
}

@Injectable({
  providedIn: 'root',
})
export class ListadosService {
  private entidades: IEntidades = this.getInitCache();

  constructor(
    private webSocketService: WebSocketService,
    private clientesService: ClientesService,
    private usuariosService: UsuariosService,
    private apiKeyService: apiKeyService,
    private dispositivosService: DispositivosService,
    private establecimientosService: EstablecimientosService,
    private lotesService: LotesService,
    private ambientesService: AmbientesService,
    private alertasService: AlertasService,
    private comandosService: ComandosService,
    private reportesService: ReportesService,
    private tipoDispositivosService: TipoDispositivosService,
    private bombeoService: BombeoService,
    private bombeoV2Service: BombeoV2Service,
    private comederosService: ComederosService,
    private animalsService: CaravanasService,
    private represasService: RepresasService,
    private cisternasService: CisternasService,
    private estacionMeteorologicasService: EstacionMeteorologicasService,
    private laminasService: SlalService,
    private pluviometrosService: PluviometrosService,
    private freatimetrosService: FreatimetrosService,
    private canalesService: CanalesService,
    private shsService: ShsService,
    private zonasService: ZonasService,
    private silobolsasService: SilobolsasService,
    private sumergiblesService: SumergiblesService,
    private evnetosService: EventosService,
    private sucesosService: SucesosService,
    private sensorRpmService: SensorRPMsService,
    private configNotificacionService: ConfigNotificacionService,
    private luminariasService: LuminariasService,
    private canieriaService: CanieriaService,
    private lanzaHumedadService: LanzaHumedadService,
  ) {
    this.subscribeWsUpdates();
  }

  // Subscribe

  public subscribe<Tipo>(
    entidad: keyof IEntidades,
    query: IQueryParam | string,
  ): Observable<Tipo> {
    const key = typeof query === 'string' ? query : JSON.stringify(query);
    const ent = this.entidades[entidad];
    if (!this.entidades[entidad]) {
      throw new Error(`No existe la entidad ${entidad}`);
    } else {
      if (!ent.keys[key]) {
        ent.keys[key] = new RequestQueue();
        // console.log(`${entidad} Nueva key ${key}`);
      }
    }
    return ent.keys[key].subscribe.asObservable() as any;
  }

  public async getLastValue(
    entidad: keyof IEntidades,
    query: IQueryParam | string,
  ): Promise<void> {
    const ent = this.entidades[entidad];
    if (!this.entidades[entidad]) {
      throw new Error(`No existe la entidad ${entidad}`);
    } else {
      if (typeof query === 'string') {
        await this.listarId(entidad, query, (ent as IRequestId).fn);
      } else {
        await this.listarQuery(entidad, query, (ent as IRequestQuery).fn);
      }
    }
  }

  // Listados Entidades
  private async listarCliente(id: string): Promise<ICliente> {
    const response = await this.clientesService
      .listarPorId(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarClientes(
    query: IQueryParam,
  ): Promise<IListado<ICliente>> {
    const response = await this.clientesService
      .listar(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarUsuario(id: string): Promise<IUsuario> {
    const response = await this.usuariosService
      .listarPorId(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarUsuarios(
    query: IQueryParam,
  ): Promise<IListado<IUsuario>> {
    const response = await this.usuariosService
      .listar(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarApikey(id: string): Promise<IAPIkey> {
    const response = await this.apiKeyService
      .listarPorId(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarApikeys(query: IQueryParam): Promise<IListado<IAPIkey>> {
    const response = await this.apiKeyService
      .listar(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarDispositivo(id: string): Promise<IDispositivo> {
    const response = await this.dispositivosService
      .listarPorId(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarDispositivoPorDeveui(
    deveui: string,
  ): Promise<IDispositivo> {
    const response = await this.dispositivosService
      .listarPorDeveui(deveui)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarDispositivos(
    query: IQueryParam,
  ): Promise<IListado<IDispositivo>> {
    const response = await this.dispositivosService
      .listar(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarDispositivosWithRegistros(
    query: IQueryParam,
  ): Promise<IListado<IDispositivoWithRegistros>> {
    const response = await this.bombeoV2Service
      .listarWithRegistros(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarEstablecimiento(id: string): Promise<IEstablecimiento> {
    const response = await this.establecimientosService
      .listarPorId(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarEstablecimientos(
    query: IQueryParam,
  ): Promise<IListado<IEstablecimiento>> {
    const response = await this.establecimientosService
      .listar(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarLote(id: string): Promise<ILote> {
    const response = await this.lotesService
      .listarPorId(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarLotes(query: IQueryParam): Promise<IListado<ILote>> {
    const response = await this.lotesService
      .listar(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarAmbiente(id: string): Promise<IAmbiente> {
    const response = await this.ambientesService
      .listarPorId(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarAmbientes(
    query: IQueryParam,
  ): Promise<IListado<IAmbiente>> {
    const response = await this.ambientesService
      .listar(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarZona(id: string): Promise<IZona> {
    const response = await this.zonasService
      .listarPorId(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarZonas(query: IQueryParam): Promise<IListado<IZona>> {
    const response = await this.zonasService
      .listar(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarAlerta(id: string): Promise<IAlerta> {
    const response = await this.alertasService
      .listarPorId(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarAlertas(query: IQueryParam): Promise<IListado<IAlerta>> {
    const response = await this.alertasService
      .listar(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarComando(id: string): Promise<IComando> {
    const response = await this.comandosService
      .listarPorId(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarComandos(
    query: IQueryParam,
  ): Promise<IListado<IComando>> {
    const response = await this.comandosService
      .listar(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarReporte(id: string): Promise<IReporte> {
    const response = await this.reportesService
      .listarPorId(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarReportes(
    query: IQueryParam,
  ): Promise<IListado<IReporte>> {
    const response = await this.reportesService
      .listar(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarTipoDispositivo(id: string): Promise<ITipoDispositivo> {
    const response = await this.tipoDispositivosService
      .listarPorId(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarTipoDispositivoPorNombre(
    nombre: string,
  ): Promise<ITipoDispositivo> {
    const response = await this.tipoDispositivosService
      .listarPorNombre(nombre)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarTipoDispositivos(
    query: IQueryParam,
  ): Promise<IListado<ITipoDispositivo>> {
    const response = await this.tipoDispositivosService
      .listar(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarEvento(id: string): Promise<IEvento> {
    const response = await this.evnetosService
      .listarPorId(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarEventos(query: IQueryParam): Promise<IListado<IEvento>> {
    const response = await this.evnetosService
      .listar(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  // BOMBEO

  private async listarBombeoCanal(id: string): Promise<IBombeoCanal> {
    const response = await this.bombeoService
      .getCanal(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarBombeoCanals(
    query: IQueryParam,
  ): Promise<IListado<IBombeoCanal>> {
    const response = await this.bombeoService
      .getCanales(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  private async listarBombeoArrancador(id: string): Promise<IBombeoArrancador> {
    const response = await this.bombeoService
      .getArrancador(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarBombeoArrancadors(
    query: IQueryParam,
  ): Promise<IListado<IBombeoArrancador>> {
    const response = await this.bombeoService
      .getArrancadores(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  // COMEDEROS

  private async listarComedero(id: string): Promise<IComedero> {
    const response = await this.comederosService
      .getComedero(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarComederos(
    query: IQueryParam,
  ): Promise<IListado<IComedero>> {
    const response = await this.comederosService
      .getComederos(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  // Silobolsas

  private async listarSilobolsa(id: string): Promise<IComedero> {
    const response = await this.silobolsasService
      .getSilobolsa(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarSilobolsas(
    query: IQueryParam,
  ): Promise<IListado<ISilobolsa>> {
    const response = await this.silobolsasService
      .getSilobolsas(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  // CARAVANA

  private async listarAnimal(id: string): Promise<IAnimal> {
    const response = await this.animalsService
      .getAnimal(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarAnimals(query: IQueryParam): Promise<IListado<IAnimal>> {
    const response = await this.animalsService
      .getAnimals(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  // CISTERNAS

  private async listarCisterna(id: string): Promise<ICisterna> {
    const response = await this.cisternasService
      .getCisterna(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarCisternas(
    query: IQueryParam,
  ): Promise<IListado<ICisterna>> {
    const response = await this.cisternasService
      .getCisternas(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  // Laminas

  private async listarLamina(id: string): Promise<ILamina> {
    const response = await this.laminasService
      .getLamina(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarLaminas(query: IQueryParam): Promise<IListado<ILamina>> {
    const response = await this.laminasService
      .getLaminas(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  // Estacion Meteorologica

  private async listarEstacionMeteorologica(
    id: string,
  ): Promise<IEstacionMeteorologica> {
    const response = await this.estacionMeteorologicasService
      .getEstacionMeteorologica(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarEstacionMeteorologicas(
    query: IQueryParam,
  ): Promise<IListado<IEstacionMeteorologica>> {
    const response = await this.estacionMeteorologicasService
      .getEstacionMeteorologicas(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  // Pluviometro

  private async listarPluviometro(id: string): Promise<IPluviometro> {
    const response = await this.pluviometrosService
      .getPluviometro(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarPluviometros(
    query: IQueryParam,
  ): Promise<IListado<IPluviometro>> {
    const response = await this.pluviometrosService
      .getPluviometros(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  // SHS

  private async listarShs(id: string): Promise<ISensorHumedadSuelo> {
    const response = await this.shsService.getShs(id).pipe(take(1)).toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarShss(
    query: IQueryParam,
  ): Promise<IListado<ISensorHumedadSuelo>> {
    const response = await this.shsService
      .getShss(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  // Freatimetro

  private async listarFreatimetro(id: string): Promise<IFreatimetro> {
    const response = await this.freatimetrosService
      .getFreatimetro(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarFreatimetros(
    query: IQueryParam,
  ): Promise<IListado<IFreatimetro>> {
    const response = await this.freatimetrosService
      .getFreatimetros(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  // Canal

  private async listarCanal(id: string): Promise<ICanal> {
    const response = await this.canalesService
      .getCanal(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarCanales(query: IQueryParam): Promise<IListado<ICanal>> {
    const response = await this.canalesService
      .getCanales(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  // Bombas Sumergibles

  private async listarSumergible(id: string): Promise<IBombaSumergible> {
    const response = await this.sumergiblesService
      .getByid(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarSumergibles(
    query: IQueryParam,
  ): Promise<IListado<IBombaSumergible>> {
    const response = await this.sumergiblesService
      .getFiltered(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  // Represas

  private async listarRepresa(id: string): Promise<IRepresa> {
    const response = await firstValueFrom(this.canalesService.getRepresa(id));
    return JSON.parse(JSON.stringify(response));
  }
  private async listarRepresas(
    query: IQueryParam,
  ): Promise<IListado<IRepresa>> {
    const response = await firstValueFrom(
      this.canalesService.getRepresaes(query),
    );
    return JSON.parse(JSON.stringify(response));
  }

  // Sucesos

  private async listarSuceso(id: string): Promise<ISuceso> {
    const response = await firstValueFrom(this.sucesosService.getById(id));
    return JSON.parse(JSON.stringify(response));
  }

  private async listarSucesos(query: IQueryParam): Promise<IListado<ISuceso>> {
    const response = await firstValueFrom(
      this.sucesosService.getFiltered(query),
    );
    return JSON.parse(JSON.stringify(response));
  }

  // Pluviometro

  private async listarSensorRPM(id: string): Promise<ISensorRPM> {
    const response = await firstValueFrom(this.sensorRpmService.getById(id));

    return JSON.parse(JSON.stringify(response));
  }
  private async listarSensorRPMs(
    query: IQueryParam,
  ): Promise<IListado<ISensorRPM>> {
    const response = await firstValueFrom(
      this.sensorRpmService.getFiltered(query),
    );
    return JSON.parse(JSON.stringify(response));
  }

  // Config notificaciones
  private async listarConfigNotificacion(
    id: string,
  ): Promise<IConfigNotificacion> {
    const response = await firstValueFrom(
      this.configNotificacionService.listarPorId(id),
    );
    return JSON.parse(JSON.stringify(response));
  }
  private async listarConfigNotificacions(
    query: IQueryParam,
  ): Promise<IListado<IConfigNotificacion>> {
    const response = await firstValueFrom(
      this.configNotificacionService.listar(query),
    );
    return JSON.parse(JSON.stringify(response));
  }

  // Luminarias
  private async listarLuminaria(id: string): Promise<ILuminaria> {
    const response = await firstValueFrom(this.luminariasService.getById(id));
    return JSON.parse(JSON.stringify(response));
  }

  private async listarLuminarias(
    query: IQueryParam,
  ): Promise<IListado<ILuminaria>> {
    const response = await firstValueFrom(
      this.luminariasService.getFiltered(query),
    );
    return JSON.parse(JSON.stringify(response));
  }

  // Canierias
  private async listarCanieria(id: string): Promise<ICanieria> {
    const response = await firstValueFrom(this.canieriaService.getCanieria(id));
    return JSON.parse(JSON.stringify(response));
  }

  private async listarCanierias(
    query: IQueryParam,
  ): Promise<IListado<ICanieria>> {
    const response = await firstValueFrom(
      this.canieriaService.getCanierias(query),
    );
    return JSON.parse(JSON.stringify(response));
  }

  // LANZA HUMEDAD

  private async listarLanzaHumedad(id: string): Promise<ILanzaHumedad> {
    const response = await this.lanzaHumedadService
      .getLanzaHumedad(id)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }
  private async listarLanzasHumedad(
    query: IQueryParam,
  ): Promise<IListado<ILanzaHumedad>> {
    const response = await this.lanzaHumedadService
      .getLanzasHumedad(query)
      .pipe(take(1))
      .toPromise();
    return JSON.parse(JSON.stringify(response));
  }

  //

  ///////////////////////////////////////////////////////////////////////////////

  // Borrar cache
  private getInitCache(): IEntidades {
    return {
      cliente: { fn: this.listarCliente.bind(this), keys: {} },
      clientes: { fn: this.listarClientes.bind(this), keys: {} },
      usuario: { fn: this.listarUsuario.bind(this), keys: {} },
      usuarios: { fn: this.listarUsuarios.bind(this), keys: {} },
      dispositivo: { fn: this.listarDispositivo.bind(this), keys: {} },
      dispositivoByDeveui: {
        fn: this.listarDispositivoPorDeveui.bind(this),
        keys: {},
      },
      dispositivos: { fn: this.listarDispositivos.bind(this), keys: {} },
      establecimiento: { fn: this.listarEstablecimiento.bind(this), keys: {} },
      establecimientos: {
        fn: this.listarEstablecimientos.bind(this),
        keys: {},
      },
      lote: { fn: this.listarLote.bind(this), keys: {} },
      lotes: { fn: this.listarLotes.bind(this), keys: {} },
      ambiente: { fn: this.listarAmbiente.bind(this), keys: {} },
      ambientes: { fn: this.listarAmbientes.bind(this), keys: {} },
      zona: { fn: this.listarZona.bind(this), keys: {} },
      zonas: { fn: this.listarZonas.bind(this), keys: {} },
      apikey: { fn: this.listarApikey.bind(this), keys: {} },
      apikeys: { fn: this.listarApikeys.bind(this), keys: {} },
      alerta: { fn: this.listarAlerta.bind(this), keys: {} },
      alertas: { fn: this.listarAlertas.bind(this), keys: {} },
      evento: { fn: this.listarEvento.bind(this), keys: {} },
      eventos: { fn: this.listarEventos.bind(this), keys: {} },
      comando: { fn: this.listarComando.bind(this), keys: {} },
      comandos: { fn: this.listarComandos.bind(this), keys: {} },
      reporte: { fn: this.listarReporte.bind(this), keys: {} },
      reportes: { fn: this.listarReportes.bind(this), keys: {} },
      tipodispositivo: { fn: this.listarTipoDispositivo.bind(this), keys: {} },
      tipodispositivoByNombre: {
        fn: this.listarTipoDispositivoPorNombre.bind(this),
        keys: {},
      },
      tipodispositivos: {
        fn: this.listarTipoDispositivos.bind(this),
        keys: {},
      },
      // BOMBEO
      bombeocanal: { fn: this.listarBombeoCanal.bind(this), keys: {} },
      bombeocanals: { fn: this.listarBombeoCanals.bind(this), keys: {} },
      bombeoarrancador: {
        fn: this.listarBombeoArrancador.bind(this),
        keys: {},
      },
      bombeoarrancadors: {
        fn: this.listarBombeoArrancadors.bind(this),
        keys: {},
      },
      alertasBombeo: { fn: this.listarAlertas.bind(this), keys: {} },
      dispositivosBombeo: { fn: this.listarDispositivos.bind(this), keys: {} },
      reportesBombeo: { fn: this.listarReportes.bind(this), keys: {} },
      // COMEDEROS
      comedero: { fn: this.listarComedero.bind(this), keys: {} },
      comederos: { fn: this.listarComederos.bind(this), keys: {} },
      alertasComedero: { fn: this.listarAlertas.bind(this), keys: {} },
      dispositivosComedero: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
      reportesComedero: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      // Silobolsas
      silobolsa: { fn: this.listarSilobolsa.bind(this), keys: {} },
      silobolsas: { fn: this.listarSilobolsas.bind(this), keys: {} },
      alertasSilobolsa: { fn: this.listarAlertas.bind(this), keys: {} },
      dispositivosSilobolsa: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
      reportesSilobolsa: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      // CARAVANA
      animal: { fn: this.listarAnimal.bind(this), keys: {} },
      animals: { fn: this.listarAnimals.bind(this), keys: {} },
      dispositivosCaravana: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
      reportesCaravana: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      alertasCaravana: { fn: this.listarAlertas.bind(this), keys: {} },
      reportesCaravanaMyT: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      // CANAL
      alertasCanal: { fn: this.listarAlertas.bind(this), keys: {} },
      dispositivosCanal: { fn: this.listarDispositivos.bind(this), keys: {} },
      reportesCanal: { fn: this.listarReportes.bind(this), keys: {} },
      // REPRESAS
      alertasRepresa: { fn: this.listarAlertas.bind(this), keys: {} },
      dispositivosRepresa: { fn: this.listarDispositivos.bind(this), keys: {} },
      reportesRepresa: { fn: this.listarReportes.bind(this), keys: {} },
      // CISTERNAS
      cisterna: { fn: this.listarCisterna.bind(this), keys: {} },
      cisternas: { fn: this.listarCisternas.bind(this), keys: {} },
      alertasBombaCisterna: {
        fn: this.listarAlertas.bind(this),
        keys: {},
      },
      alertasNivelCisterna: { fn: this.listarAlertas.bind(this), keys: {} },
      dispositivosBombaCisterna: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
      dispositivosNivelCisterna: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
      reportesBombaCisterna: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      reportesNivelCisterna: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      reportesCanalBombeoV2: { fn: this.listarReportes.bind(this), keys: {} },
      reportesPresionAgua: { fn: this.listarReportes.bind(this), keys: {} },
      // SLA
      alertasSla: { fn: this.listarAlertas.bind(this), keys: {} },
      dispositivosSla: { fn: this.listarDispositivos.bind(this), keys: {} },
      reportesSla: { fn: this.listarReportes.bind(this), keys: {} },
      // Estacion Meteorologica
      estacionMeteorologica: {
        fn: this.listarEstacionMeteorologica.bind(this),
        keys: {},
      },
      estacionMeteorologicas: {
        fn: this.listarEstacionMeteorologicas.bind(this),
        keys: {},
      },
      reportesEstacionMeteorologica: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      alertasEstacionMeteorologica: {
        fn: this.listarAlertas.bind(this),
        keys: {},
      },
      dispositivosEstacionMeteorologica: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
      comandosEstacionMeteorologica: {
        fn: this.listarComandos.bind(this),
        keys: {},
      },
      // Laminas
      lamina: {
        fn: this.listarLamina.bind(this),
        keys: {},
      },
      laminas: {
        fn: this.listarLaminas.bind(this),
        keys: {},
      },
      // Pluviometros
      alertasPluviometro: {
        fn: this.listarAlertas.bind(this),
        keys: {},
      },
      dispositivosPluviometro: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
      reportesPluviometro: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      comandosPluviometro: {
        fn: this.listarComandos.bind(this),
        keys: {},
      },
      pluviometro: {
        fn: this.listarPluviometro.bind(this),
        keys: {},
      },
      pluviometros: {
        fn: this.listarPluviometros.bind(this),
        keys: {},
      },
      // Freatimetro
      alertasFreatimetro: {
        fn: this.listarAlertas.bind(this),
        keys: {},
      },
      dispositivosFreatimetro: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
      reportesFreatimetro: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      comandosFreatimetro: {
        fn: this.listarComandos.bind(this),

        keys: {},
      },
      freatimetro: {
        fn: this.listarFreatimetro.bind(this),
        keys: {},
      },
      freatimetros: {
        fn: this.listarFreatimetros.bind(this),
        keys: {},
      },
      // Canal
      canal: {
        fn: this.listarCanal.bind(this),
        keys: {},
      },
      canales: {
        fn: this.listarCanales.bind(this),
        keys: {},
      },
      // SHS
      dispositivosShs: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
      reportesShs: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      sensorHumedadSuelo: {
        fn: this.listarShs.bind(this),
        keys: {},
      },
      sensorHumedadSuelos: {
        fn: this.listarShss.bind(this),
        keys: {},
      },
      bombaSumergible: {
        fn: this.listarSumergible.bind(this),
        keys: {},
      },
      bombaSumergibles: {
        fn: this.listarSumergibles.bind(this),
        keys: {},
      },
      // Bombeo V2
      dispositivosBombeoV2: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
      //BOMBEO V2 con ultimos registros
      dispositivosBombeoV2UltimosRegistros: {
        fn: this.listarDispositivosWithRegistros.bind(this),
        keys: {},
      },
      // Presion de agua
      dispositivosPresionAgua: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
      // Represas
      represa: {
        fn: this.listarRepresa.bind(this),
        keys: {},
      },
      represas: {
        fn: this.listarRepresas.bind(this),
        keys: {},
      },
      // Suceso
      suceso: {
        fn: this.listarSuceso.bind(this),
        keys: {},
      },
      sucesos: {
        fn: this.listarSucesos.bind(this),
        keys: {},
      },
      // RPM
      dispositivosSensorRPM: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
      reportesSensorRPM: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      sensorRPM: {
        fn: this.listarSensorRPM.bind(this),
        keys: {},
      },
      sensorRPMs: {
        fn: this.listarSensorRPMs.bind(this),
        keys: {},
      },
      // LANZA HUMEDAD
      dispositivosLanzaHumedad: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
      lanzaHumedad: {
        fn: this.listarLanzaHumedad.bind(this),
        keys: {},
      },
      lanzasHumedad: {
        fn: this.listarLanzasHumedad.bind(this),
        keys: {},
      },
      reportesLanzaHumedad: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      alertasLanzaHumedad: {
        fn: this.listarAlertas.bind(this),
        keys: {},
      },
      // notificaciones
      configNotificacion: {
        fn: this.listarConfigNotificacion.bind(this),
        keys: {},
      },
      configNotificacions: {
        fn: this.listarConfigNotificacions.bind(this),
        keys: {},
      },
      // Luminarias
      luminaria: {
        fn: this.listarLuminaria.bind(this),
        keys: {},
      },
      luminarias: {
        fn: this.listarLuminarias.bind(this),
        keys: {},
      },
      reportesLuminarias: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      alertasLuminarias: {
        fn: this.listarAlertas.bind(this),
        keys: {},
      },
      dispositivosLuminarias: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },

      // canierias
      canieria: {
        fn: this.listarCanieria.bind(this),
        keys: {},
      },
      canierias: {
        fn: this.listarCanierias.bind(this),
        keys: {},
      },
      reportesCanierias: {
        fn: this.listarReportes.bind(this),
        keys: {},
      },
      alertasCanierias: {
        fn: this.listarAlertas.bind(this),
        keys: {},
      },
      dispositivosCanierias: {
        fn: this.listarDispositivos.bind(this),
        keys: {},
      },
    };
  }

  public borrarCache() {
    this.entidades = this.getInitCache();
  }

  // Actualizar Entidades
  private async actualizarQuery(entidad: keyof IEntidades): Promise<void> {
    const ent = this.entidades[entidad] as IRequestQuery;
    for (const key in ent.keys) {
      if (ent.keys[key].requests === 0) {
        if (Object.prototype.hasOwnProperty.call(ent.keys, key)) {
          ent.keys[key].cache = undefined;
          const query = JSON.parse(key);
          if (ent.keys[key].subscribe.observers.length) {
            ent.keys[key].requests++;
            while (ent.keys[key].requests) {
              ent.keys[key].cache = undefined;
              await this.listarQuery(entidad, query, ent.fn);
              ent.keys[key].requests--;
            }
          }
        }
      } else if (ent.keys[key].requests < 2) {
        ent.keys[key].requests++;
      }
    }
  }

  private async actualizarId(
    entidad: keyof IEntidades,
    id?: string,
  ): Promise<void> {
    if (id) {
      const ent = this.entidades[entidad] as IRequestId;
      if (ent.keys[id]) {
        if (ent.keys[id].requests === 0) {
          ent.keys[id].cache = undefined;
          if (ent.keys[id].subscribe.observers.length) {
            ent.keys[id].requests++;
            while (ent.keys[id].requests) {
              ent.keys[id].cache = undefined;
              await this.listarId(entidad, id, ent.fn);
              ent.keys[id].requests--;
            }
          }
        } else if (ent.keys[id].requests < 2) {
          ent.keys[id].requests++;
        }
      }
    }
  }

  // Listados Generales

  private async listarQuery(
    entidad: keyof IEntidades,
    query: IQueryParam,
    fn: (query: IQueryParam) => Promise<any>,
  ): Promise<void> {
    const ent = this.entidades[entidad];
    const key = JSON.stringify(query);
    if (!ent.keys[key]?.cache) {
      const response = await fn(query);
      ent.keys[key].cache = JSON.parse(JSON.stringify(response));
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    ent.keys[key].subscribe.next(ent.keys[key]?.cache!);
  }

  private async listarId(
    entidad: keyof IEntidades,
    id: string,
    fn: (id: string) => Promise<any>,
  ): Promise<void> {
    const ent = this.entidades[entidad];
    if (!ent.keys[id].cache) {
      const response = await fn(id);
      ent.keys[id].cache = JSON.parse(JSON.stringify(response));
    }
    ent.keys[id].subscribe.next(ent.keys[id].cache!);
  }

  // Suscripcion a WS Service para eliminar cache y actualizar entidades
  private subscribeWsUpdates() {
    this.webSocketService.getMessage().subscribe({
      next: this.handleUpdateResponse.bind(this),
    });
  }
  private handleUpdateResponse(message: ISocketMessage) {
    const tipoDispositivo = (message.body?.tipoDispositivo ||
      message.tipoDispositivo) as TipoDispositivo;
    if (message.paths?.includes('clientes') || message.path === 'clientes') {
      this.actualizarQuery('clientes');
      this.actualizarId('cliente', message.body?._id);
    }
    if (message.paths?.includes('usuarios') || message.path === 'usuarios') {
      this.actualizarQuery('usuarios');
      this.actualizarId('usuario', message.body?._id);
    }
    if (message.paths?.includes('apikeys') || message.path === 'apikey') {
      this.actualizarQuery('apikeys');
      this.actualizarId('apikey', message.body?._id);
    }
    if (
      message.paths?.includes('establecimientos') ||
      message.path === 'establecimientos'
    ) {
      this.actualizarQuery('establecimientos');
      this.actualizarId('establecimiento', message.body?._id);
    }
    if (message.paths?.includes('lotes') || message.path === 'lotes') {
      this.actualizarQuery('lotes');
      this.actualizarId('lote', message.body?._id);
    }
    if (message.paths?.includes('ambientes') || message.path === 'ambientes') {
      this.actualizarQuery('ambientes');
      this.actualizarId('ambiente', message.body?._id);
    }
    if (message.paths?.includes('zonas') || message.path === 'zonas') {
      this.actualizarQuery('zonas');
      this.actualizarId('zona', message.body?._id);
    }
    if (
      message.paths?.includes('tipodispositivos') ||
      message.path === 'tipodispositivos'
    ) {
      this.actualizarQuery('tipodispositivos');
      this.actualizarId('tipodispositivo', message.body?._id);
      this.actualizarId;
    }
    // Compartidos
    if (
      message.paths?.includes('dispositivos') ||
      message.path === 'dispositivos'
    ) {
      this.actualizarQuery('dispositivos');
      this.actualizarId('dispositivo', message.body?._id);
      this.actualizarId('dispositivoByDeveui', message.body?.deveui);
      if (tipoDispositivo === 'Estacion de Bombeo') {
        this.actualizarQuery('dispositivosBombeo');
      }
      if (tipoDispositivo === 'Nivel de Canal') {
        this.actualizarQuery('dispositivosCanal');
      }
      if (tipoDispositivo === 'Nivel de Comederos') {
        this.actualizarQuery('dispositivosComedero');
      }
      if (tipoDispositivo === 'Caravana') {
        this.actualizarQuery('dispositivosCaravana');
      }
      if (tipoDispositivo === 'Nivel de Represas') {
        this.actualizarQuery('dispositivosRepresa');
      }
      if (tipoDispositivo === 'Nivel de Láminas de Agua') {
        this.actualizarQuery('dispositivosSla');
      }
      if (tipoDispositivo === 'Nivel de Cisterna') {
        this.actualizarQuery('dispositivosNivelCisterna');
      }
      if (tipoDispositivo === 'Estacion Meteorologica') {
        this.actualizarQuery('dispositivosEstacionMeteorologica');
      }
      if (tipoDispositivo === 'Pluviometro') {
        this.actualizarQuery('dispositivosPluviometro');
      }
      if (tipoDispositivo === 'Sensor Humedad de Suelo') {
        this.actualizarQuery('dispositivosShs');
      }
      if (tipoDispositivo === 'Freatimetro') {
        this.actualizarQuery('dispositivosFreatimetro');
      }
      if (tipoDispositivo === 'Bomba de Cisterna') {
        this.actualizarQuery('dispositivosBombaCisterna');
      }
      if (tipoDispositivo === 'Estacion de Bombeo V2') {
        this.actualizarQuery('dispositivosBombeoV2');
        this.actualizarQuery('dispositivosBombeoV2UltimosRegistros');
      }
      if (tipoDispositivo === 'Presion de Agua') {
        this.actualizarQuery('dispositivosPresionAgua');
      }
      if (
        tipoDispositivo === 'Luminaria' ||
        tipoDispositivo === 'Luminaria Wellness'
      ) {
        this.actualizarQuery('dispositivosLuminarias');
        this.actualizarQuery('luminarias');
      }

      if (tipoDispositivo === 'Sensor de Caudal') {
        this.actualizarQuery('dispositivosCanierias');
        this.actualizarQuery('canierias');
      }
      if (tipoDispositivo === 'Lanza de Humedad') {
        this.actualizarQuery('dispositivosLanzaHumedad');
        this.actualizarQuery('lanzasHumedad');
      }
    }
    if (message.paths?.includes('alertas') || message.path === 'alertas') {
      this.actualizarQuery('alertas');
      this.actualizarId('alerta', message.body?._id);
      if (tipoDispositivo === 'Estacion de Bombeo') {
        this.actualizarQuery('alertasBombeo');
      }
      if (tipoDispositivo === 'Nivel de Canal') {
        this.actualizarQuery('alertasCanal');
      }
      if (tipoDispositivo === 'Nivel de Comederos') {
        this.actualizarQuery('alertasComedero');
      }
      if (tipoDispositivo === 'Caravana') {
        this.actualizarQuery('alertasCaravana');
      }
      if (tipoDispositivo === 'Nivel de Represas') {
        this.actualizarQuery('alertasRepresa');
      }
      if (tipoDispositivo === 'Nivel de Láminas de Agua') {
        this.actualizarQuery('alertasSla');
      }
      if (tipoDispositivo === 'Nivel de Cisterna') {
        this.actualizarQuery('alertasNivelCisterna');
      }
      if (tipoDispositivo === 'Estacion Meteorologica') {
        this.actualizarQuery('alertasEstacionMeteorologica');
      }
      if (tipoDispositivo === 'Pluviometro') {
        this.actualizarQuery('alertasPluviometro');
      }
      if (tipoDispositivo === 'Freatimetro') {
        this.actualizarQuery('alertasFreatimetro');
      }
      if (tipoDispositivo === 'Sensor de Caudal') {
        this.actualizarQuery('alertasCanierias');
      }
      if (tipoDispositivo === 'Lanza de Humedad') {
        this.actualizarQuery('alertasLanzaHumedad');
      }
    }
    if (message.paths?.includes('eventos') || message.path === 'eventos') {
      this.actualizarQuery('eventos');
      this.actualizarId('evento', message.body?._id);
      // if (tipoDispositivo === 'Estacion de Bombeo') {
      //   this.actualizarQuery('alertasBombeo');
      // }
      // if (tipoDispositivo === 'Nivel de Canal') {
      //   this.actualizarQuery('alertasCanal');
      // }
      // if (tipoDispositivo === 'Nivel de Comederos') {
      //   this.actualizarQuery('alertasComedero');
      // }
      // if (tipoDispositivo === 'Caravana') {
      //   this.actualizarQuery('alertasCaravana');
      // }
      // if (tipoDispositivo === 'Nivel de Represas') {
      //   this.actualizarQuery('alertasRepresa');
      // }
      // if (tipoDispositivo === 'Nivel de Láminas de Agua') {
      //   this.actualizarQuery('alertasSla');
      // }
      // if (tipoDispositivo === 'Nivel de Cisterna') {
      //   this.actualizarQuery('alertasNivelCisterna');
      // }
      // if (tipoDispositivo === 'Estacion Meteorologica') {
      //   this.actualizarQuery('alertasEstacionMeteorologica');
      // }
      // if (tipoDispositivo === 'Pluviometro') {
      //   this.actualizarQuery('alertasPluviometro');
      // }
      // if (tipoDispositivo === 'Freatimetro') {
      //   this.actualizarQuery('alertasFreatimetro');
      // }
    }
    if (message.paths?.includes('comandos') || message.path === 'comandos') {
      this.actualizarQuery('comandos');
      this.actualizarId('comando', message.body?._id);
      if (tipoDispositivo === 'Estacion Meteorologica') {
        this.actualizarQuery('comandosEstacionMeteorologica');
      }
      if (tipoDispositivo === 'Pluviometro') {
        this.actualizarQuery('comandosPluviometro');
      }
      if (tipoDispositivo === 'Freatimetro') {
        this.actualizarQuery('comandosFreatimetro');
      }
    }
    if (message.paths?.includes('reportes') || message.path === 'reportes') {
      this.actualizarQuery('reportes');
      this.actualizarId('reporte', message.body?._id);

      if (tipoDispositivo === 'Estacion de Bombeo') {
        this.actualizarQuery('reportesBombeo');
      }
      if (tipoDispositivo === 'Nivel de Canal') {
        this.actualizarQuery('reportesCanal');
      }
      if (tipoDispositivo === 'Nivel de Comederos') {
        this.actualizarQuery('reportesComedero');
      }
      if (tipoDispositivo === 'Caravana') {
        this.actualizarQuery('reportesCaravana');
      }
      if (tipoDispositivo === 'Caravana MyT') {
        this.actualizarQuery('reportesCaravanaMyT');
      }
      if (tipoDispositivo === 'Nivel de Represas') {
        this.actualizarQuery('reportesRepresa');
      }
      if (tipoDispositivo === 'Nivel de Láminas de Agua') {
        this.actualizarQuery('reportesSla');
      }
      if (tipoDispositivo === 'Estacion Meteorologica') {
        this.actualizarQuery('reportesEstacionMeteorologica');
      }
      if (tipoDispositivo === 'Pluviometro') {
        this.actualizarQuery('reportesPluviometro');
      }
      if (tipoDispositivo === 'Freatimetro') {
        this.actualizarQuery('reportesFreatimetro');
      }
      if (tipoDispositivo === 'Sensor Humedad de Suelo') {
        this.actualizarQuery('reportesShs');
      }
      if (tipoDispositivo === 'Estacion de Bombeo V2') {
        this.actualizarQuery('reportesCanalBombeoV2');
      }
      if (tipoDispositivo === 'Sensor RPM') {
        this.actualizarQuery('reportesSensorRPM');
      }
      if (tipoDispositivo === 'Lanza de Humedad') {
        this.actualizarQuery('reportesLanzaHumedad');
      }
      if (
        tipoDispositivo === 'Luminaria' ||
        tipoDispositivo === 'Luminaria Wellness' ||
        tipoDispositivo === 'Luminaria Horatech'
      ) {
        this.actualizarQuery('reportesLuminarias');
      }
      if (tipoDispositivo === 'Nivel de Cisterna') {
        this.actualizarQuery('reportesNivelCisterna');
      }
      if (tipoDispositivo === 'Sensor de Caudal') {
        this.actualizarQuery('reportesCanierias');
      }
      if (tipoDispositivo === 'Presion de Agua') {
        this.actualizarQuery('reportesPresionAgua');
      }
    }
    // BOMBEO
    if (
      message.paths?.includes('bombeocanals') ||
      message.path === 'bombeocanals'
    ) {
      this.actualizarQuery('bombeocanals');
      this.actualizarId('bombeocanal', message.body?._id);
    }
    if (
      message.paths?.includes('bombeoarrancadors') ||
      message.path === 'bombeoarrancadors'
    ) {
      this.actualizarQuery('bombeoarrancadors');
      this.actualizarId('bombeoarrancador', message.body?._id);
    }

    // COMEDEROS
    if (message.paths?.includes('comederos') || message.path === 'comederos') {
      this.actualizarQuery('comederos');
      this.actualizarId('comedero', message.body?._id);
    }

    // Silobolsas
    if (
      message.paths?.includes('silobolsas') ||
      message.path === 'silobolsas'
    ) {
      this.actualizarQuery('silobolsas');
      this.actualizarId('silobolsa', message.body?._id);
    }

    // CARAVANA
    if (message.paths?.includes('animals') || message.path === 'animals') {
      this.actualizarQuery('animals');
      this.actualizarId('animal', message.body?._id);
    }

    // CISTERNA
    if (message.paths?.includes('cisternas') || message.path === 'cisternas') {
      this.actualizarQuery('cisternas');
      this.actualizarId('cisterna', message.body?._id);
      this.actualizarQuery('dispositivosBombaCisterna');
      this.actualizarQuery('dispositivosNivelCisterna');
    }

    // Estacion Meteorologica
    if (
      message.paths?.includes('estacionmeteorologicas') ||
      message.path === 'estacionmeteorologicas'
    ) {
      this.actualizarQuery('estacionMeteorologicas');
      this.actualizarId('estacionMeteorologica', message.body?._id);
    }

    // Lamina
    if (message.paths?.includes('laminas') || message.path === 'laminas') {
      this.actualizarQuery('laminas');
      this.actualizarId('lamina', message.body?._id);
    }

    // Pluviometro
    if (
      message.paths?.includes('pluviometros') ||
      message.path === 'pluviometros'
    ) {
      this.actualizarQuery('pluviometros');
      this.actualizarId('pluviometro', message.body?._id);
    }

    // SHS
    if (message.paths?.includes('shs') || message.path === 'shs') {
      this.actualizarQuery('sensorHumedadSuelos');
      this.actualizarId('sensorHumedadSuelo', message.body?._id);
    }

    // Freatimetro
    if (
      message.paths?.includes('freatimetros') ||
      message.path === 'freatimetros'
    ) {
      this.actualizarQuery('freatimetros');
      this.actualizarId('freatimetro', message.body?._id);
    }

    // Canales
    if (message.paths?.includes('canales') || message.path === 'canales') {
      this.actualizarQuery('canales');
      this.actualizarId('canal', message.body?._id);
    }

    // Sumergibles
    if (
      message.paths?.includes('bombaSumergibles') ||
      message.path === 'bombaSumergibles'
    ) {
      this.actualizarQuery('bombaSumergibles');
      this.actualizarId('bombaSumergible', message.body?._id);
    }

    // Represas (canal)
    if (message.paths?.includes('represas') || message.path === 'represas') {
      this.actualizarQuery('represas');
      this.actualizarId('represa', message.body?._id);
    }

    // Sucesos
    if (message.paths?.includes('sucesos') || message.path === 'sucesos') {
      this.actualizarQuery('sucesos');
      this.actualizarId('suceso', message.body?._id);
    }

    // RPM
    if (
      message.paths?.includes('sensorRPMs') ||
      message.path === 'sensorRPMs'
    ) {
      this.actualizarQuery('sensorRPMs');
      this.actualizarId('sensorRPM', message.body?._id);
    }

    // Lanza de Humedad
    if (
      message.paths?.includes('lanzasHumedad') ||
      message.path === 'lanzasHumedad' ||
      message.path === 'lanzahumedad' ||
      message.path?.includes('lanzahumedad')
    ) {
      this.actualizarQuery('lanzasHumedad');
      this.actualizarId('lanzaHumedad', message.body?._id);
    }

    // CONFIG Notificaciones
    if (
      message.paths?.includes('confignotificacions') ||
      message.path === 'confignotificacions'
    ) {
      this.actualizarQuery('configNotificacions');
      this.actualizarId('configNotificacion', message.body?._id);
    }
    if (
      message.paths?.includes('luminarias') ||
      message.path === 'luminarias'
    ) {
      this.actualizarQuery('luminarias');
      this.actualizarId('luminaria', message.body?._id);
    }
  }
}
